import { z } from 'zod'

export const enum Permission {
  Read = 'read',
  Write = 'write',
  None = 'none'
}

export function round (value: number, decimalPlaces = 2): number {
  let multiplier = Math.pow(10, decimalPlaces)
  return Math.round(value * multiplier) / multiplier
}

export function validatePhoneNumber (value: string): boolean {
  let pattern = /^(?:\d{3}\.\d{3}\.\d{4}|\d{10})$/
  return pattern.test(value)
}

export function formatPhoneNumber (value: string): string {
  let pattern = /^\+1(\d{3})(\d{3})(\d{4})/
  return value.replace(pattern, '$1.$2.$3')
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getBaseSchema = (role: string | null) => {
  return z.object({
    first_name: z.string({
      required_error: 'First name is required'
    })
      .trim()
      .min(1, 'First name is required'),

    last_name: z.string({
      required_error: 'Last name is required'
    })
      .trim()
      .min(1, 'Last name is required'),

    suffix: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? value.trim() || null
        : value,

      z.string()
        .trim()
        .nullish()
        .default(null)
    ),

    email: z.string({
      required_error: 'Email is required'
    })
      .trim()
      .toLowerCase()
      .email('Email should have valid format')
      .min(1, 'Email is required'),

    contact: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? value.trim() || null
        : value,

      z.string()
        .trim()
        .nullish()
        .default(null)
        .refine(value => typeof value === 'string' ? validatePhoneNumber(value) : true, {
          message: 'Phone should have valid format'
        })
        .transform(value => typeof value === 'string' ? `+1${value.replace(/[^\d]/g, '')}` : value)
    ),

    phone: z.string({
      required_error: 'Phone is required'
    })
      .trim()
      .min(1, 'Phone is required')
      .refine(value => typeof value === 'string' ? validatePhoneNumber(value) : true, {
        message: 'Phone should have valid format'
      })
      .transform(value => typeof value === 'string' ? `+1${value.replace(/[^\d]/g, '')}` : value),

    onduty: z.boolean()
      .default(false),

    monitoring: z.boolean()
      .default(false),

    oncall: z.boolean()
      .default(false),

    concierge: z.boolean()
      .default(false),

    practitioner_notifications: z.boolean()
      .default(false),

    trend: z.boolean()
      .default(true),

    behavioral: z.enum([ Permission.Read, Permission.Write, Permission.None ])
      .default(Permission.None),

    uba_care: z.enum([ Permission.Read, Permission.Write, Permission.None ])
      .default(Permission.None),

    mobile_management: z.enum([ Permission.Read, Permission.Write, Permission.None ])
      .default(Permission.None),

    organization: z.string({
      required_error: 'Organization is required',
      invalid_type_error: 'Organization is required'
    })
      .min(1, 'Organization is required'),

    supervisor: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? value.trim() || null
        : value,

      z.string()
        .nullish()
        .default(null)
    ),

    director: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? value.trim() || null
        : value,

      z.string()
        .nullish()
        .default(null)
    ),

    role: z.string({
      required_error: 'Role is required',
      invalid_type_error: 'Role is required'
    })
      .min(1, 'Role is required'),

    group: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? value.trim() || null
        : value,

      z.string()
        .nullish()
        .default(null)
    ),

    prn: z.boolean()
      .default(false),

    target_load: z.union([
      z.string(),
      z.number()
    ])
      .pipe(
        z.coerce.number({
          required_error: 'Target load is required',
          invalid_type_error: 'Please provide integer from 0 to 99'
        })
          .int()
          .min(0, 'Target load must be greater than or equal to 0')
          .max(99, 'Target load must be less than or equal to 99')
      ),

    expected_billable_hours: z.union([
      z.string(),
      z.number()
    ])
      .pipe(
        z.coerce.number({
          required_error: 'Expected billable hours is required',
          invalid_type_error: 'Please provide a number from 0 to 999 (2 decimal places allowed)'
        })
          .min(0, 'Expected billable hours must be greater than or equal to 0')
          .max(999, 'Expected billable hours must be less than or equal to 999')
          .transform(value => round(value, 2))
      ),

    organizations: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? [ value ]
        : value,

      z.string({
        invalid_type_error: 'At least one organization is required'
      })
        .array()
        .min(1, 'At least one organization is required')
    ),
    client_tags: z.preprocess(
      (value: unknown) => typeof value === 'string'
        ? [ value ]
        : value,

      z.string()
        .array()
        .min(role === 'broker_access' ? 1 : 0, 'Compassion view required for Broker Access')
    )
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getExistingUserSchema = (role: string | null) => {
  const baseSchema = getBaseSchema(role)

  return baseSchema.extend({
    id: z.string({
      required_error: 'ID is required'
    }),

    enabled: z.boolean()
      .default(true)
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getNewUserSchema = (role: string | null) => {
  const baseSchema = getBaseSchema(role)

  return baseSchema.extend({
    password: z.string({
      required_error: 'Password is required'
    })
      .trim()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters long')
      .refine(value => typeof value === 'string' ? /[a-z]/.test(value) : true, 'Password must contain at least one lowercase letter')
      .refine(value => typeof value === 'string' ? /[A-Z]/.test(value) : true, 'Password must contain at least one uppercase letter')
      .refine(value => typeof value === 'string' ? /\d/.test(value) : true, 'Password must contain at least one digit'),

    password_confirmation: z.string({
      required_error: 'Password confirmation is required'
    })
      .trim()
      .min(1, 'Password confirmation is required')
  })
    .refine(data => data.password === data.password_confirmation, {
      message: 'Should match to password',
      path: [ 'password_confirmation' ]
    })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getSchema = (role: string | null) => {
  return z.union([
    getExistingUserSchema(role),
    getNewUserSchema(role)
  ])
}

export type Values = z.infer<ReturnType<typeof getSchema>>
